import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type PrimaryGender = 'Male' | 'Female' | null;

interface PrimaryGenderContextValue {
  primaryGender: PrimaryGender;
  setPrimaryGender: (gender: PrimaryGender) => void;
}

const PrimaryGenderContext = createContext<PrimaryGenderContextValue | undefined>(undefined);

interface PrimaryGenderProviderProps {
  children: ReactNode;
}

export const PrimaryGenderProvider: React.FC<PrimaryGenderProviderProps> = ({ children }) => {
  const { email } = useAuth();
  const [primaryGender, setPrimaryGenderState] = useState<PrimaryGender>(null);

  const storageKey = email ? `primary_gender_${email}` : null;

  // 初始化：从 localStorage 加载主 gender
  useEffect(() => {
    if (!storageKey) {
      setPrimaryGenderState(null);
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setPrimaryGenderState(null);
        return;
      }
      if (raw === 'Male' || raw === 'Female') {
        setPrimaryGenderState(raw);
      } else {
        setPrimaryGenderState(null);
      }
    } catch {
      setPrimaryGenderState(null);
    }
  }, [storageKey]);

  const setPrimaryGender = (gender: PrimaryGender) => {
    setPrimaryGenderState(gender);
    if (!storageKey) return;
    try {
      if (!gender) {
        window.localStorage.removeItem(storageKey);
      } else {
        window.localStorage.setItem(storageKey, gender);
      }
    } catch {
      // ignore localStorage errors
    }
  };

  return (
    <PrimaryGenderContext.Provider value={{ primaryGender, setPrimaryGender }}>
      {children}
    </PrimaryGenderContext.Provider>
  );
};

export const usePrimaryGender = (): PrimaryGenderContextValue => {
  const ctx = useContext(PrimaryGenderContext);
  if (!ctx) {
    throw new Error('usePrimaryGender must be used within a PrimaryGenderProvider');
  }
  return ctx;
};



